import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Lock, Sparkles, Crown, Loader2, Check, Headphones, Bot } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useLanguage } from "../i18n/LanguageContext";
import { trackBeginCheckout, trackPremiumGateView } from "../lib/analytics";
import { getPaywallVariant } from "../utils/paywallAB";
import RBLPaymentButton from "./pro/RBLPaymentButton";

import { API } from "../lib/api";
// API now resolved at runtime via /lib/api.js
/**
 * PremiumGate — Cinematic blur-overlay paywall.
 *
 * Props:
 *   feature: 'listen' | 'mentor'  → controls description
 *   children: ReactNode (will be hidden behind blur when locked)
 *   blur: bool (default true) — set false to render gate standalone (no children blocked)
 */
export const PremiumGate = ({ feature = "listen", children, blur = true, trialUsed = false }) => {
  const { user, isPro, openLogin, token } = useAuth();
  const { t, lang } = useLanguage();
  const p = t.pro;
  const ui = t.academy_ui || {};
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // A/B bucket: deterministic per user, sticky per anonymous browser.
  const variant = useMemo(() => getPaywallVariant(user?.id || null), [user?.id]);

  // Resolve copy for current variant with graceful fallback to control.
  const copy = useMemo(() => {
    const isB = variant === "B";
    return {
      description:
        feature === "mentor"
          ? (isB && p.gate_mentor_b) || p.gate_mentor
          : (isB && p.gate_listen_b) || p.gate_listen,
      trialUsedTitle:
        feature === "mentor"
          ? (isB && p.trial_used_mentor_title_b) || p.trial_used_mentor_title
          : (isB && p.trial_used_listen_title_b) || p.trial_used_listen_title,
      trialUsedSub: (isB && p.trial_used_sub_b) || p.trial_used_sub,
      ctaUnlock: (isB && p.cta_unlock_b) || p.cta_unlock,
    };
  }, [variant, feature, p]);

  // Telemetry — fire one "view" event per mount of the gate.
  useEffect(() => {
    if (isPro) return;
    try { trackPremiumGateView(feature); } catch {}
    // Async logging — never block UI.
    (async () => {
      try {
        await fetch(`${API}/paywall/ab/event`, {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ variant, feature, action: "view", lang }),
        });
      } catch {}
    })();
  }, [feature, isPro, variant, token, lang]);

  if (isPro) return <>{children}</>;

  const isGuest = user === false || user == null;

  const startCheckout = async () => {
    if (isGuest) { openLogin(); return; }
    setError(null); setLoading(true);
    // Fire conversion telemetry BEFORE redirect — fire-and-forget.
    try {
      fetch(`${API}/paywall/ab/event`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ variant, feature, action: "checkout_click", lang }),
        keepalive: true,
      }).catch(() => {});
    } catch {}
    try {
      trackBeginCheckout({ value: 39.90, plan: "pro_lifetime", feature });
      const res = await fetch(`${API}/payments/checkout`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          origin_url: window.location.origin,
          plan: "pro_lifetime",
        }),
      });
      // Clone before reading — some platform scripts (e.g. emergent-main.js)
      // install fetch interceptors that consume the body. The clone keeps
      // a fresh readable stream for us.
      const data = await res.clone().json();
      if (!res.ok) throw new Error(data?.detail || "Checkout failed");
      window.location.href = data.url;
    } catch (e) {
      setError(e.message || "Error");
      setLoading(false);
    }
  };

  const FeatureIcon = feature === "mentor" ? Bot : Headphones;
  const description = copy.description;

  return (
    <div className="relative" data-testid={`premium-gate-${feature}`} data-ab-variant={variant}>
      {/* Hidden / blurred content underneath */}
      {blur && children && (
        <div className="pointer-events-none select-none opacity-25 blur-[2px]">
          {children}
        </div>
      )}

      {/* Overlay card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
        className={`${blur && children ? "absolute inset-0 flex items-center justify-center" : ""}`}
      >
        <div className="relative w-full max-w-lg overflow-hidden rounded-2xl border border-orange-400/30 bg-gradient-to-br from-zinc-950/95 to-black/90 p-5 backdrop-blur-xl shadow-[0_0_40px_rgba(255,140,0,0.18)] md:p-7">
          {/* accent */}
          <span className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-orange-400/70 to-transparent" />

          <div className="mb-3 flex items-center gap-2">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-orange-400/40 bg-orange-400/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-orange-200">
              <Crown className="h-3 w-3" />
              {trialUsed ? "✨ TRIAL USADO" : p.gate_title}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.04] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white/60">
              <FeatureIcon className="h-3 w-3" />
              {feature === "mentor" ? "AI MENTOR" : "AUDIO"}
            </span>
          </div>

          <h3 className="font-display text-xl font-black leading-tight tracking-tight text-white md:text-2xl">
            {trialUsed ? copy.trialUsedTitle : description}
          </h3>
          {trialUsed && (
            <p className="mt-2 text-sm text-white/65">{copy.trialUsedSub}</p>
          )}

          {/* Includes */}
          <ul className="mt-4 space-y-1.5">
            {p.includes.map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-white/70 md:text-sm">
                <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-400" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          {/* Price + CTA */}
          <div className="mt-5 flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-orange-300/70">
                {p.price_label}
              </div>
              <div className="font-display text-3xl font-black text-white">
                {p.price}
              </div>
              <div className="text-[11px] text-white/40">{p.lifetime}</div>
            </div>
            <button
              onClick={startCheckout}
              disabled={loading}
              data-testid="premium-unlock-btn"
              className="group relative inline-flex items-center justify-center gap-2 overflow-hidden rounded-xl bg-gradient-to-r from-orange-500 to-amber-600 px-6 py-3 text-xs font-black uppercase tracking-[0.25em] text-black shadow-[0_0_28px_rgba(255,140,0,0.4)] transition-all hover:-translate-y-0.5 hover:shadow-[0_0_50px_rgba(255,140,0,0.6)] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {isGuest ? p.cta_login_first : copy.ctaUnlock}
              <span className="pointer-events-none absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/40 to-transparent transition-transform duration-1000 group-hover:translate-x-full" />
            </button>
          </div>

          {error && (
            <div className="mt-3 rounded-lg border border-red-400/30 bg-red-400/5 px-3 py-2 text-xs text-red-300" data-testid="premium-error">
              {error}
            </div>
          )}

          {/* On-chain R₿L payment rail — wallet transfer to treasury (-50%) */}
          <RBLPaymentButton />
        </div>
      </motion.div>
    </div>
  );
};

export default PremiumGate;
